'use client';

import {
  CampaignVariant,
  parseCampaignVariant,
} from '@schuelerhilfe/shared/src/enums/campaign';
import { createContext, useContext, useEffect, type ReactNode } from 'react';

export { CampaignVariant } from '@schuelerhilfe/shared/src/enums/campaign';

const COOKIE_NAME = 'sh_campaign';
const MAX_AGE = 60 * 60 * 24 * 365; // 1 year

const CampaignContext = createContext<CampaignVariant>(CampaignVariant.A);

export function useCampaign(): CampaignVariant {
  return useContext(CampaignContext);
}

function readCookieValue(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  return match?.split('=')[1]?.toLowerCase();
}

function writeCookie(value: CampaignVariant): void {
  document.cookie = `${COOKIE_NAME}=${value.toLowerCase()}; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
}

/**
 * The variant the *rendered Page* is in (SH-471). Page-scoped: each route
 * mounts it through `LiveRenderTree` with the variant of the Page that won
 * Page Selection, so a visitor's stored campaign cannot restyle a Page that
 * has no variant of its own. No state — a new `variant` on a client-side
 * navigation takes effect immediately.
 */
export function CampaignProvider({
  children,
  variant,
}: {
  children: ReactNode;
  variant: CampaignVariant;
}) {
  return (
    <CampaignContext.Provider value={variant}>
      {children}
    </CampaignContext.Provider>
  );
}

/**
 * Visitor-scoped persistence of the campaign signal: captures `?campaign=`
 * into the `sh_campaign` cookie, strips it from the URL, and refreshes the
 * stored value's lifetime. Mounted once in the `(main)` layout. It renders
 * nothing and decides nothing about what a page looks like — that is
 * `CampaignProvider`'s, from the Page.
 *
 * `visitorVariant` is the variant the request's tag bag carried, the fallback
 * persisted on a first visit with no cookie yet.
 */
export function CampaignCookieSync({
  visitorVariant,
}: {
  visitorVariant: CampaignVariant;
}) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromParam = parseCampaignVariant(params.get('campaign'));

    if (fromParam) {
      // An explicit ?campaign= is the user's intent and overwrites whatever is
      // stored. Persist `fromParam` rather than `visitorVariant`: if a cached
      // page was served, `visitorVariant` may be the server's stale answer and
      // would discard the variant that was actually requested.
      console.log(`[Campaign] Variant ${fromParam} requested via URL param`);
      writeCookie(fromParam);

      params.delete('campaign');
      const search = params.toString();
      const url = window.location.pathname + (search ? `?${search}` : '');
      window.history.replaceState(null, '', url);
    } else {
      // Never let a server-resolved variant clobber a stored one.
      // `visitorVariant` falls back to A whenever the request carried no
      // campaign signal — including when a CDN serves a cached variant-A page
      // to a visitor whose cookie says B. Writing the cookie's own value back
      // keeps the stored variant intact while still refreshing its one-year
      // TTL, and falls back to `visitorVariant` only on a first visit with no
      // cookie yet.
      const stored = parseCampaignVariant(readCookieValue());
      writeCookie(stored ?? visitorVariant);
    }
  }, [visitorVariant]);

  return null;
}
